import { type Session } from '@ory/client-fetch';
import { Flex } from '@radix-ui/themes';
import { type QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  retainSearchParams,
  stripSearchParams,
} from '@tanstack/react-router';
import React, { type PropsWithChildren } from 'react';
import { Services } from '~/services';
import { Organisation, OrganisationsPage } from '~/services/organisations-api/types';
import { z } from 'zod';
import { searchWithOrg } from '~/utils/orgSlug';
import { routesWithoutOrganisation } from './routeExemptions';
// import { MaintenanceModal } from '~/components/MaintenanceModal';

type RouteContext = {
  queryClient: QueryClient;
  session: Session | null;
  isSsoSetupRequired: boolean;
  organisation: Organisation | undefined;
  organisations: OrganisationsPage | undefined;
  services: Services;
};

const rootSearchParams = z.object({
  org: z.string().optional(),
  orgSlug: z.string().optional(),
  a: z.boolean().optional(),
});

// const TanStackRouterDevtools =
//   import.meta.env.MODE === 'production'
//     ? () => null // Render nothing in production
//     : React.lazy(() =>
//         // Lazy load in development
//         import('@tanstack/router-devtools').then((res) => ({
//           default: res.TanStackRouterDevtools,
//         }))
//       );

function doesRequireOrganisation(path: string) {
  return !routesWithoutOrganisation.some((route) => path.startsWith(route));
}

export const Route = createRootRouteWithContext<RouteContext>()({
  component: RootComponent,
  errorComponent: () => <div>Error</div>,
  validateSearch: rootSearchParams,
  search: {
    middlewares: [retainSearchParams(['org', 'a']), stripSearchParams(['orgSlug'])],
  },
  loader: async ({ context, location }) => {
    const { pathname, search } = location;
    const searchParams = rootSearchParams.parse(search);

    if (doesRequireOrganisation(pathname)) {
      if (context.organisation?.slug && !searchParams.org) {
        // Keep the rest of the query. Replacing search with only `org` drops params such as content and flow.
        throw redirect({
          to: location.pathname,
          search: searchWithOrg(search, context.organisation.slug),
        });
      }

      const { organisations: organisationsPage } = context;
      if (!organisationsPage) {
        // failed to load
        console.error('Organisation required but /organisations undefined');
      } else if ((organisationsPage.organisations?.length ?? 0) === 0) {
        // user does not have an org. Onboarding returns to the app root, not this path.
        const returnTo = new URL('/', window.location.origin).href;
        window.location.href = `${import.meta.env.VITE_AUTH_URL}/onboarding?return_to=${encodeURIComponent(returnTo)}`;
      }
    }
  },
});

function Layout({ children }: PropsWithChildren) {
  return (
    <Flex height="100%" direction="column">
      {children}
    </Flex>
  );
}

function RootComponent() {
  return (
    <>
      <Layout>
        <Outlet />
        {/* <MaintenanceModal title="System Maintenance" showModal={true} closeModal={() => {}} /> */}
      </Layout>
      {/* <TanStackRouterDevtools position="bottom-left" /> */}
    </>
  );
}
